import type { FormEvent } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { useCurrentUser } from '@klp/auth';
import { ClientRoutes } from '@klp/client/config';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { ErrorNotification } from '@klp/errorNotification';
import { Main } from '@klp/layout';

import { KLPSupportForm } from '../classes/KLPSupportForm';
import { KlpSupportFormForm } from '../components/KlpSupportFormForm';
import { SupportFormRoutes } from '../config';
import type { ISupportApplicationRequestParams } from '../interfaces';
import { useGetSupportFormQuery, usePostSupportFormMutation } from '../rtkApi';

export const ApplicationFormAdd: React.FC = () => {
    const history = useHistory();
    const { formId } = useParams<ISupportApplicationRequestParams>();
    const { selectedClient } = useAppParams();

    const { currentUser: user } = useCurrentUser();

    const { data: formData } = useGetSupportFormQuery(formId!, {
        skip: !formId,
    });
    const klpSupportForm = formData?.klpSupportForm;

    const [postSupportForm, { data: savedFormData, isSuccess }] =
        usePostSupportFormMutation();
    const klpSupportFormSaved = savedFormData?.klpSupportForm;

    useEffect(() => {
        if (user && selectedClient && !formId) {
            history.push(SupportFormRoutes.formAdd(selectedClient.kundeNummer));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, selectedClient, formId]);

    useEffect(() => {
        if (isSuccess && klpSupportFormSaved) {
            history.push(SupportFormRoutes.formView(klpSupportFormSaved.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, klpSupportFormSaved]);

    const onSubmit = (
        event: FormEvent<HTMLFormElement>,
        supportForm: KLPSupportForm,
    ) => {
        event.preventDefault();
        postSupportForm({ klpSupportForm: supportForm });
    };

    const onCancel = () => {
        if (formId) {
            history.push(SupportFormRoutes.formView(formId));
        }
    };

    // Create enriched form without mutating Redux state
    const enrichedForm = useMemo(() => {
        const baseForm = klpSupportForm || KLPSupportForm.empty();

        if (user && klpSupportForm) {
            return {
                ...baseForm,
                email: user.email,
                personApplying: `${user.firstName} ${user.lastName}`,
                kundeNummer: selectedClient?.kundeNummer || '',
                companyName: selectedClient?.kundeName || '',
            };
        }

        return baseForm;
    }, [klpSupportForm, user, selectedClient]);

    const renderForm = (supportForm: KLPSupportForm) => (
        <KlpSupportFormForm
            klpSupportForm={supportForm}
            onSubmit={onSubmit}
            onCancel={onCancel}
        />
    );

    // Require client selection for authorized users with loaded form
    if (user && klpSupportForm && !selectedClient) {
        return (
            <Main>
                <ErrorNotification
                    title="clientSelect.errorNoClientsSelected.title"
                    description="clientSelect.errorNoClientsSelected.description"
                    backTarget={AppRoutes.mainPage()}
                    backTitle="general.goBackToFrontpage"
                    mainTarget={ClientRoutes.clientSelect()}
                    mainTitle="clientSelect.chooseClient"
                    returnUrl={true}
                />
            </Main>
        );
    }

    return <Main>{renderForm(enrichedForm)}</Main>;
};
